import { Tabs } from '@sparemin/blockhead';
import React, { useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import SocialShareModalAICaptionsField from 'containers/SocialShareModal/SocialShareModalAICaptionsField';
import SocialShareModalBody from 'containers/SocialShareModal/SocialShareModalBody';
import { useSocialShareModal } from 'containers/SocialShareModal/SocialShareModalContext';
import { videoDurationSecondsSelector } from 'redux/modules/download';
import {
  onClickInstagramPostModalCTA,
  onInstagramPostModalLoad,
} from 'redux/modules/mixpanel';
import { instagramIsPostingSelector } from 'redux/modules/social';
import { Dispatch } from 'redux/types';
import { socialShareModalBlock as block } from '../../../../utils';
import { InstagramPostType } from '../../types';
import {
  INSTAGRAM_MAX_DESCRIPTION_CHARS,
  INSTAGRAM_MAX_STORY_DURATION_SEC,
} from './constants';

export type InstagramPostComposerStepProps = {
  postType: InstagramPostType;
  onPostTypeChange: (postType: InstagramPostType) => void;
};

const InstagramPostComposerStep: React.FC<InstagramPostComposerStepProps> = ({
  postType,
  onPostTypeChange,
}) => {
  const hasTrackedTabChangeRef = useRef(false);

  const isPosting = useSelector(instagramIsPostingSelector);
  const videoDurationSec = useSelector(videoDurationSecondsSelector);

  const {
    description,
    isSchedulingPost,
    setDescription,
  } = useSocialShareModal();

  const isStoriesAllowed = videoDurationSec <= INSTAGRAM_MAX_STORY_DURATION_SEC;
  const loadingTitle = isSchedulingPost ? 'Scheduling Video' : 'Posting Video';
  const isLoading = isSchedulingPost || isPosting;

  const dispatch = useDispatch<Dispatch>();

  const handleTabChange = useCallback(
    (tabKey: InstagramPostType) => {
      onPostTypeChange(tabKey);

      if (hasTrackedTabChangeRef.current) {
        return;
      }

      hasTrackedTabChangeRef.current = true;

      if (tabKey === 'story') {
        dispatch(onInstagramPostModalLoad('ReelsSelected'));
      }

      if (tabKey === 'feed') {
        dispatch(onClickInstagramPostModalCTA('SelectFeed'));
      }
    },
    [dispatch, onPostTypeChange],
  );

  return (
    <Tabs
      selectedKey={postType}
      disabledKeys={!isStoriesAllowed ? ['story'] : []}
      className={block('instagram-post-composer-step-container')}
      onSelectionChange={handleTabChange}
    >
      <Tabs.Item key="feed" title="Feed">
        <SocialShareModalBody {...{ loadingTitle, isLoading }}>
          <SocialShareModalAICaptionsField
            inputProps={{
              value: description,
              maxChars: INSTAGRAM_MAX_DESCRIPTION_CHARS,
              onChange: setDescription,
            }}
          />
        </SocialShareModalBody>
      </Tabs.Item>

      <Tabs.Item
        key="story"
        title="Story"
        tooltip={
          !isStoriesAllowed
            ? `Videos longer than ${INSTAGRAM_MAX_STORY_DURATION_SEC} seconds cannot be posted as a story`
            : undefined
        }
      >
        <SocialShareModalBody
          {...{ loadingTitle, isLoading }}
          showRegenerateTextButton={false}
        />
      </Tabs.Item>
    </Tabs>
  );
};

export default InstagramPostComposerStep;
